import { createRootRoute, Link, Navigate, Outlet, useRouterState } from '@tanstack/react-router';
import { Megaphone, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { AppSidebar } from '@/components/AppSidebar';
import { NotFound } from '@/components/errors/NotFound';
import { Button } from '@/components/ui/button';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { Spinner } from '@/components/ui/spinner';
import { AUTH_MODE } from '@/lib/firebase';

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFound,
});

/** The magic-link landing page has to render before any auth status is known. */
const SIGN_IN_PATH = '/portal/signin';

function RootComponent() {
  const { status } = useAuth();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // Has to come before every status branch below: a sponsor clicking a sign-in
  // link is `signed-out` until this page finishes the exchange, so gating on
  // status first would never let them reach it.
  if (pathname === SIGN_IN_PATH) return <Outlet />;

  if (status === 'loading')
    return (
      <FullScreen>
        <Spinner className="size-6" />
      </FullScreen>
    );

  // A signed-out visitor anywhere under /portal wants the sponsor door, not the
  // staff one. Without this they get "Campaign Console — DearDoc staff only"
  // and a Google button that cannot help them.
  if (status === 'signed-out' && pathname.startsWith('/portal')) {
    return <Navigate to={SIGN_IN_PATH} />;
  }
  if (status === 'signed-out') return <SignIn />;
  if (status === 'forbidden') return <AccessDenied />;

  // A sponsor session renders an entirely different shell — see AppSidebar's
  // `variant` — and never the admin `Outlet` tree below. Any non-portal path
  // (typically `/`, left over from a bookmark or a shared link) redirects
  // into it rather than mounting an admin page that would just 403.
  if (status === 'sponsor' && !pathname.startsWith('/portal')) {
    return <Navigate to="/portal" />;
  }

  return (
    <SidebarProvider>
      <AppSidebar variant={status === 'sponsor' ? 'portal' : 'admin'} />
      <SidebarInset>
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  );
}

function FullScreen({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-svh items-center justify-center p-6">{children}</div>;
}

/**
 * Sign-in is rendered in place rather than as its own route. The console has a
 * single origin behind two identity providers, so a dedicated `/login` URL
 * would only add a redirect dance to get back where you started.
 *
 * Both routes are offered plainly and neither is labelled with an audience.
 * An earlier version led with "Campaign Console — DearDoc staff only" and gave
 * sponsors a small grey link underneath, which read as an afterthought for the
 * people this platform sells to. The fix is not to caption who uses which door:
 * anyone arriving here already knows which credential they hold, and spelling it
 * out only dates the screen the moment a third audience appears. The subtitle
 * describes the platform instead.
 *
 * The email-link route is a `Link` rather than a handler because it goes to
 * `/portal/signin`, which completes a Firebase email-link exchange — a
 * different flow from a Google popup, not a variant of one.
 */
function SignIn() {
  const { signIn, error } = useAuth();

  return (
    <FullScreen>
      <div className="w-full max-w-sm space-y-6 text-center">
        <div className="mx-auto flex size-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
          <Megaphone className="size-6" />
        </div>
        <div className="space-y-1">
          <h1 className="text-xl font-semibold">DearDoc Media Network</h1>
          <p className="text-sm text-muted-foreground">
            Campaign delivery and reporting across the DearDoc network.
          </p>
        </div>

        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
        )}

        <div className="space-y-2">
          <Button className="w-full" onClick={() => void signIn()}>
            {AUTH_MODE === 'emulator' ? 'Continue as local developer' : 'Sign in with Google'}
          </Button>
          <Button asChild variant="outline" className="w-full">
            <Link to={SIGN_IN_PATH}>Sign in with an email link</Link>
          </Button>
        </div>

        {AUTH_MODE === 'emulator' && (
          <p className="text-xs text-muted-foreground">
            Emulator mode — no identity provider. Set{' '}
            <code className="font-mono">VITE_AUTH_MODE=firebase</code> for Google SSO, or{' '}
            <code className="font-mono">VITE_DEV_SPONSOR=spo_&lt;id&gt;</code> to open the sponsor
            portal.
          </p>
        )}
      </div>
    </FullScreen>
  );
}

function AccessDenied() {
  const { user, signOut, error } = useAuth();

  return (
    <FullScreen>
      <div className="w-full max-w-sm space-y-6 text-center">
        <div className="mx-auto flex size-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
          <ShieldAlert className="size-6" />
        </div>
        <div className="space-y-1">
          <h1 className="text-xl font-semibold">You don’t have access yet</h1>
          <p className="text-sm text-muted-foreground">
            {user?.email ? `You’re signed in as ${user.email}, but this account ` : 'This account '}
            {error ?? 'is not yet set up for the console or a sponsor dashboard.'} Your DearDoc
            contact can sort that out.
          </p>
        </div>
        <Button variant="outline" className="w-full" onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>
    </FullScreen>
  );
}
